import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import axios from "../../../../Utils/axiosInstance";
import { useLockBodyScroll } from "./../../../Hooks/useLockBodyScroll";
import { useClickOutside } from './../../../Hooks/useClickOutside';

const AddressSelectModal = ({
  showModal,
  closeModal,
  addresses,
  setAddresses,
  setDefaultAddress,
  refreshAddresses,
  openAddModal,
}) => {
  const [animate, setAnimate] = useState(false);
  const [loadingId, setLoadingId] = useState(null);
  const domNode = useClickOutside(() => handleClose());

  useEffect(() => {
    if (showModal) setTimeout(() => setAnimate(true), 10);
  }, [showModal]);

  const handleClose = () => {
    setAnimate(false);
    setTimeout(closeModal, 250);
  };


  const handleSelectDefault = async (id) => {
    if (loadingId) return;
    try {
      setLoadingId(id);
      await axios.patch(`/address/${id}/default`);
      await refreshAddresses();
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingId(null);
    }
  };

  // Group addresses by type, preserving original order
  const officeAddresses = addresses.filter((a) => a.addressType === "Office");
  const warehouseAddresses = addresses.filter(
    (a) => a.addressType === "WareHouse",
  );

  if (!showModal) return null;

  return createPortal(
    <div
      className={`fixed inset-0 z-50 flex items-end md:items-center justify-center
      bg-black/30 backdrop-blur-sm transition-opacity duration-300
      ${animate ? "opacity-100" : "opacity-0"}`}
    >
      <div
        ref={domNode}
        className={`bg-white w-full md:w-[520px] md:rounded-3xl rounded-t-3xl shadow-2xl flex flex-col transition-all duration-300 overflow-hidden
        ${animate
            ? "scale-100 translate-y-0"
            : "scale-95 md:translate-y-4 translate-y-full"
          }`}
        style={{ maxHeight: "90vh" }}
      >
        {/* HEADER */}
        <div className="px-5 py-4 bg-primaryColor text-white flex items-center flex-shrink-0">
          <button onClick={handleClose} className="text-2xl leading-none">
            ←
          </button>
          <div className="flex-1 text-center font-semibold text-lg">
            Manage Addresses
          </div>
        </div>

        {/* BODY */}
        <div className="p-5 space-y-5 overflow-auto flex-1">
          {/* ── OFFICE SECTION ── */}
          <Section
            title="Office Locations"
            icon="🏢"
            color="purple"
            addresses={officeAddresses}
            loadingId={loadingId}
            onSelectDefault={handleSelectDefault}
            onEdit={(addr) => {
              setAnimate(false);
              setTimeout(() => {
                closeModal();
                openAddModal("Office", addr);
              }, 250);
            }}
            onAdd={() => {
              setAnimate(false);
              setTimeout(() => {
                closeModal();
                openAddModal("Office", null);
              }, 250);
            }}
          />

          {/* ── WAREHOUSE SECTION ── */}
          <Section
            title="Warehouse Locations"
            icon="🏭"
            color="green"
            addresses={warehouseAddresses}
            loadingId={loadingId}
            onSelectDefault={handleSelectDefault}
            onEdit={(addr) => {
              setAnimate(false);
              setTimeout(() => {
                closeModal();
                openAddModal("WareHouse", addr);
              }, 250);
            }}
            onAdd={() => {
              setAnimate(false);
              setTimeout(() => {
                closeModal();
                openAddModal("WareHouse", null);
              }, 250);
            }}
          />
        </div>

        {/* FOOTER */}
        <div className="p-5 border-t flex-shrink-0">
          <button
            onClick={handleClose}
            className="w-full py-3 border rounded-xl text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

/* ─────────────────────────────────────────────────────────────
   Section sub-component — renders one address type group
───────────────────────────────────────────────────────────── */
const colorMap = {
  purple: {
    bg: "bg-[#F3EEF4]",
    border: "border-purple-200",
    badge: "bg-purple-100 text-purple-700",
    ring: "ring-purple-400",
    dot: "bg-purple-500",
    addBorder: "border-purple-300 text-purple-600 hover:bg-purple-50",
  },
  green: {
    bg: "bg-[#EAF3EC]",
    border: "border-green-200",
    badge: "bg-green-100 text-green-700",
    ring: "ring-green-400",
    dot: "bg-green-500",
    addBorder: "border-green-300 text-green-600 hover:bg-green-50",
  },
};

const Section = ({
  title,
  icon,
  color,
  addresses,
  loadingId,
  onSelectDefault,
  onEdit,
  onAdd,
}) => {
  const c = colorMap[color];

  return (
    <div>
      {/* Section header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-base">{icon}</span>
          <p className="text-sm font-semibold text-gray-700">{title}</p>
          {addresses.length > 0 && (
            <span
              className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${c.badge}`}
            >
              {addresses.length}
            </span>
          )}
        </div>
        <button
          onClick={onAdd}
          className={`text-xs font-semibold border px-3 py-1 rounded-full transition-colors ${c.addBorder}`}
        >
          + Add New
        </button>
      </div>

      {/* Address cards */}
      {addresses.length === 0 ? (
        <div
          onClick={onAdd}
          className={`rounded-2xl p-4 cursor-pointer border-2 border-dashed border-gray-200 hover:border-gray-300 text-center transition-colors`}
        >
          <p className="text-sm text-gray-400">
            No {title.toLowerCase()} saved yet
          </p>
          <p className="text-xs text-gray-300 mt-0.5">Tap to add one</p>
        </div>
      ) : (
        <div className="space-y-2">
          {addresses.map((addr) => (
            <AddressCard
              key={addr._id}
              addr={addr}
              colorConfig={c}
              loadingId={loadingId}
              onSelectDefault={onSelectDefault}
              onEdit={onEdit}
            />
          ))}
        </div>
      )}
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────
   Individual address card
───────────────────────────────────────────────────────────── */
const AddressCard = ({
  addr,
  colorConfig: c,
  loadingId,
  onSelectDefault,
  onEdit,
}) => {
  const isLoading = loadingId === addr._id;

  return (
    <div
      className={`rounded-2xl p-4 border transition-all duration-200
        ${addr.isDefault ? `${c.bg} ${c.border} ring-1 ${c.ring}` : "border-gray-200 hover:border-gray-300 bg-white"}
      `}
    >
      <div className="flex items-start gap-3">
        {/* Default indicator dot */}
        <div className="flex-shrink-0 mt-1">
          {addr.isDefault ? (
            <span className={`block w-2.5 h-2.5 rounded-full ${c.dot}`} />
          ) : (
            <span className="block w-2.5 h-2.5 rounded-full border-2 border-gray-300" />
          )}
        </div>

        {/* Address text */}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-800 truncate">
            {addr.addressLine1}
          </p>
          {addr.addressLine2 && (
            <p className="text-xs text-gray-500 truncate mt-0.5">
              {addr.addressLine2}
            </p>
          )}
          <p className="text-xs text-gray-400 mt-0.5">
            {[addr.city, addr.state, addr.pincode].filter(Boolean).join(", ")}
          </p>

          {/* Actions row */}
          <div className="flex items-center gap-3 mt-2">
            {/* Set Default */}
            {!addr.isDefault && (
              <button
                onClick={() => onSelectDefault(addr._id)}
                disabled={!!loadingId}
                className="text-[11px] font-semibold text-primaryColor hover:underline disabled:opacity-50 transition-opacity"
              >
                {isLoading ? "Setting..." : "Set as Default"}
              </button>
            )}
            {addr.isDefault && (
              <span className="text-[11px] font-semibold text-gray-400">
                ✓ Default
              </span>
            )}

            <span className="text-gray-200 text-xs">|</span>

            {/* Edit */}
            <button
              onClick={() => onEdit(addr)}
              className="text-[11px] font-semibold text-gray-500 hover:text-gray-700 transition-colors"
            >
              Edit
            </button>
          </div>
        </div>

        {/* Loading spinner on the right */}
        {isLoading && (
          <div className="flex-shrink-0 mt-1">
            <svg
              className="w-4 h-4 animate-spin text-primaryColor"
              viewBox="0 0 24 24"
              fill="none"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v8H4z"
              />
            </svg>
          </div>
        )}
      </div>
    </div>
  );
};

export default AddressSelectModal;
